import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  User,
  Calendar,
  Download,
  Clock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AuditTrailModalInline - 21 CFR Part 11 Compliant Audit Trail Modal
 * This is the inline version used in DashboardPage (matching its exact styling)
 */
const AuditTrailModalInline = ({
  isOpen,
  onClose,
  selectedDocForVersion,
  auditLogs,
  getAuthHeaders,
}) => {
  const handleDownloadPDF = async () => {
    if (!selectedDocForVersion) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/${selectedDocForVersion.id}/audit-report`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Audit_Trail_${selectedDocForVersion.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate audit report');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Clock className="w-5 h-5 text-red-600" />
            Audit Trail - {selectedDocForVersion?.name}
            <span className="text-xs bg-green-500/20 text-green-600 border border-green-500/30 px-2 py-0.5 rounded-full ml-2">
              21 CFR Part 11
            </span>
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Complete audit history for regulatory compliance
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg mb-4 text-sm">
            <p className="font-medium text-blue-300 mb-1">21 CFR Part 11 Compliance</p>
            <p className="text-red-600 text-xs">All actions are timestamped, attributed to specific users, and cannot be altered. This audit trail provides tamper-evident records.</p>
          </div>
          <ScrollArea className="h-96">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center py-8">No audit logs found for this document</p>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, index) => (
                  <div key={log.id} className="p-4 bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        log.action.includes('UPLOAD') ? 'bg-green-500/20 text-green-600 border border-green-500/30' :
                        log.action.includes('DELETE') ? 'bg-red-500/20 text-red-600 border border-red-500/30' :
                        log.action.includes('DOWNLOAD') || log.action.includes('VIEW') ? 'bg-red-500/20 text-red-600 border border-red-500/30' :
                        log.action.includes('WORKFLOW') ? 'bg-purple-500/20 text-purple-600 border border-purple-500/30' :
                        log.action.includes('SIGN') ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                        log.action.includes('PASSWORD') ? 'bg-amber-500/20 text-amber-600 border border-amber-500/30' :
                        log.action.includes('UPDATE') || log.action.includes('EDIT') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                        'bg-slate-200 text-slate-700 dark:text-slate-300'
                      }`}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">#{auditLogs.length - index}</span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{log.details}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-300 dark:border-slate-600">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.user_name} ({log.user_email})
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        <DialogFooter className="border-t border-slate-300 dark:border-slate-600 pt-4">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">
            Total entries: {auditLogs.length}
          </p>
          <Button 
            variant="outline" 
            onClick={handleDownloadPDF}
            className="rounded-full bg-red-600 border-red-500 text-slate-900 dark:text-slate-100 hover:bg-red-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuditTrailModalInline;
